import NeptuneElement from "../../NeptuneElement.js";

class Checkbox extends NeptuneElement {

    templateMarkup = `
        <div>
            <div>
                <input type="checkbox" />
                <label></label>
            </div>
            <div>
                <span></span>
                <ul></ul>
            </div>
        </div>
    `;

    componentCssUrl = '/src/components/form/checkbox/checkbox.css';

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        
        if (!this.hasAttribute('name')) {
            throw new Error('neptune-checkbox: "name" attribute is required.');
        }
        
        this.processInitialAttributesAfterRender(Checkbox.observedAttributes);
        this.setupAccessibility();
        this.setEventListeners();
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['name', 'value', 'checked', 'disabled', 'label', 'indeterminate', 'required', 'autocomplete', 'error', 'error-text', 'supporting-text'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        
        if (!this.isRendered) {
            return;
        }
        
        this.processAttribute(name, oldValue, newValue);
    }   

    setupAccessibility() {
        const checkboxInput = this.shadowRoot.querySelector('input[type="checkbox"]');
        const label = this.shadowRoot.querySelector('label');
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');
        
        if (!checkboxInput || !label) return;

        const uniqueId = `neptune-checkbox-${Math.random().toString(36).substr(2, 9)}`;
        
        checkboxInput.id = uniqueId;
        label.setAttribute('for', uniqueId);

        if (errorList) {
            errorList.id = `neptune-checkbox-errors-${Math.random().toString(36).substr(2, 9)}`;
        }

        if (supportingText) {
            supportingText.id = `neptune-checkbox-support-${Math.random().toString(36).substr(2, 9)}`;
        }

        const describedBy = [errorList?.id, supportingText?.id].filter(Boolean).join(' ');
        if (describedBy) {
            checkboxInput.setAttribute('aria-describedby', describedBy);
        }
    }

    updateMessages() {
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (!errorList || !supportingText) {
            return;
        }

        errorList.setAttribute('aria-live', 'polite');
        supportingText.setAttribute('aria-live', 'polite');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter((item) => item.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach((error) => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
                errorList.style.display = 'block';
            } else {
                errorList.style.display = 'none';
            }

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            if (supportingTextValue) {
                supportingText.textContent = supportingTextValue;
                supportingText.classList.add('visually-hidden-a11y');
                supportingText.style.display = 'block';
            } else {
                supportingText.style.display = 'none';
            }
        } else {
            errorList.innerHTML = '';
            errorList.style.display = 'none';

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            supportingText.textContent = supportingTextValue;
            supportingText.classList.remove('visually-hidden-a11y');
            supportingText.style.display = supportingTextValue ? 'block' : 'none';
        }
    }

    processAttribute(name, oldValue, newValue) {
        const checkboxInput = this.shadowRoot.querySelector('input[type="checkbox"]');
        if(!checkboxInput) {
            return;
        }

        switch (name) {
            case 'name': {
                if(newValue) {
                    checkboxInput.name = newValue;
                } else {
                    checkboxInput.removeAttribute('name');
                    throw new Error('neptune-checkbox: "name" attribute is required.');
                }
                break;
            }
            case 'value': {
                if(newValue) {
                    checkboxInput.value = newValue;
                } else {
                    checkboxInput.removeAttribute('value');
                }
                break;
            }
            case 'checked': {
                const isChecked = this.hasAttribute('checked');
                checkboxInput.checked = isChecked;
                break;
            }
            case 'disabled': {
                const isDisabled = this.hasAttribute('disabled');
                checkboxInput.disabled = isDisabled;
                break;
            }
            case 'indeterminate': {
                const isIndeterminate = this.hasAttribute('indeterminate');
                checkboxInput.indeterminate = isIndeterminate;
                break;
            }
            case 'required': {
                this.hasAttribute('required')
                    ? checkboxInput.setAttribute('required', '')
                    : checkboxInput.removeAttribute('required');
                break;
            }
            case 'error': {
                this.hasAttribute('error')
                    ? checkboxInput.setAttribute('aria-invalid', 'true')
                    : checkboxInput.removeAttribute('aria-invalid');
                this.updateMessages();
                break;
            }
            case 'error-text':
            case 'supporting-text': {
                this.updateMessages();
                break;
            }
            case 'autocomplete': {
                const autocomplete = this.getAttribute('autocomplete');
                autocomplete !== null 
                    ? checkboxInput.setAttribute('autocomplete', autocomplete) 
                    : checkboxInput.setAttribute('autocomplete', 'off');
                break;
            }
            case 'label': {
                const labelSpan = this.shadowRoot.querySelector('label');
                if(newValue) {
                    labelSpan.textContent = newValue;
                    labelSpan.style.display = 'inline';
                } else {
                    labelSpan.textContent = '';
                    labelSpan.style.display = 'none';
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    setEventListeners() {
        this.removeEventListeners();
        const checkboxInput = this.shadowRoot.querySelector('input[type="checkbox"]');
        if(!checkboxInput) {
            return;
        }

        this.handleCheckboxChange = (e) => {
            if(this.hasAttribute('disabled')) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            }

            if(checkboxInput.checked) {
                this.setAttribute('checked', '');
            } else {
                this.removeAttribute('checked');
            }

            if(this.hasAttribute('indeterminate')) {
                this.removeAttribute('indeterminate');
                checkboxInput.indeterminate = false;
            }

            this.dispatchEvent(new CustomEvent('neptune-checkbox-change', {
                bubbles: true,
                composed: true,
                detail: {
                    value: this.getAttribute('value'),
                    name: this.getAttribute('name'),
                    checked: checkboxInput.checked
                }
            }));
        };

        checkboxInput.addEventListener('change', this.handleCheckboxChange);
    }
    
    removeEventListeners() {
        const checkboxInput = this.shadowRoot.querySelector('input[type="checkbox"]');
        if(checkboxInput){
            checkboxInput.removeEventListener('change', this.handleCheckboxChange);
        }
    }
}

customElements.define('neptune-checkbox', Checkbox);

export default Checkbox;